import { sleep } from "@/lib/utils";

import { ApiError } from "../errors";
import type { Api, ShopMember, User } from "../types";
import { STOREFRONT_OWNER_USERS } from "./sellers";

let fixtures: User[] = [
	{
		id: "usr_demo_anna",
		email: "anna@osmix.ru",
		phone: "+7 (999) 123-45-67",
		name: "Анна Мастерова",
		shopName: "Мастерская Анны",
		shopId: "shop-handmade",
		role: "verified_seller",
		onboardingStatus: "active",
		createdAt: "2026-01-15T09:00:00Z",
	},
	{
		id: "usr_demo_admin",
		email: "admin@osmix.ru",
		phone: "+7 (999) 000-00-00",
		name: "Платформа",
		role: "admin",
		onboardingStatus: "active",
		createdAt: "2026-01-01T00:00:00Z",
	},
	{
		id: "usr_demo_order_manager",
		email: "orders@osmix.ru",
		phone: "+7 (999) 555-44-33",
		name: "Ольга Заказова",
		role: "shop-order-manager",
		onboardingStatus: "active",
		createdAt: "2026-02-01T09:00:00Z",
	},
	// Owners for the remaining storefronts, sourced from the seller fixtures so
	// each storefront resolves its verifying seller (User.shopId) offline too.
	...STOREFRONT_OWNER_USERS,
];

const memberships: ShopMember[] = [
	{
		id: "shm_demo_owner",
		shopId: "shop-handmade",
		userId: "usr_demo_anna",
		name: "Анна Мастерова",
		email: "anna@osmix.ru",
		phone: "+7 (999) 123-45-67",
		role: "shop-owner",
		status: "active",
		invitedAt: "2026-01-15T09:00:00Z",
		joinedAt: "2026-01-15T09:00:00Z",
	},
	{
		id: "shm_demo_order_manager",
		shopId: "shop-handmade",
		userId: "usr_demo_order_manager",
		name: "Ольга Заказова",
		email: "orders@osmix.ru",
		phone: "+7 (999) 555-44-33",
		role: "shop-order-manager",
		status: "active",
		invitedAt: "2026-02-01T09:00:00Z",
		joinedAt: "2026-02-01T09:10:00Z",
	},
];

export const mockStaffForShop = (shopId: string): ShopMember[] =>
	memberships.filter((member) => member.shopId === shopId && member.status === "active");

export const updateMockStaffRole = (memberId: string, role: ShopMember["role"]): ShopMember => {
	const member = memberships.find((item) => item.id === memberId);
	if (!member) throw new ApiError("Staff member not found", 404, "NOT_FOUND");
	if (member.role === "shop-owner") throw new ApiError("Owner role cannot be changed", 400, "VALIDATION_ERROR");
	member.role = role;
	return member;
};

export const removeMockStaffMember = (memberId: string): void => {
	const member = memberships.find((item) => item.id === memberId);
	if (!member) throw new ApiError("Staff member not found", 404, "NOT_FOUND");
	if (member.role === "shop-owner") throw new ApiError("Owner cannot be removed", 400, "VALIDATION_ERROR");
	member.status = "removed";
	member.removedAt = new Date().toISOString();
};

const find = (id: string): User => {
	const user = fixtures.find((u) => u.id === id);
	if (!user) throw new ApiError("User not found", 404, "NOT_FOUND");
	return user;
};

export const users: Api["users"] = {
	async list() {
		// Tiny synthetic latency keeps loading-state UX honest in dev.
		await sleep(150);
		return fixtures;
	},
	async page(filter) {
		await sleep(150);
		const q = filter.search?.trim().toLowerCase();
		const rows = fixtures.filter(
			(u) =>
				(!filter.role || u.role === filter.role) &&
				(!filter.blocked || !!u.blockedAt) &&
				(!q || [u.name, u.email, u.phone].join(" ").toLowerCase().includes(q)),
		);
		const start = (filter.page - 1) * filter.pageSize;
		return {
			items: rows.slice(start, start + filter.pageSize),
			total: rows.length,
			page: filter.page,
			pageSize: filter.pageSize,
			totalPages: Math.ceil(rows.length / filter.pageSize),
		};
	},
	async get(id) {
		await sleep(150);
		return find(id);
	},
	async create(input) {
		await sleep(250);
		if (fixtures.some((user) => user.email.toLowerCase() === input.email.toLowerCase())) {
			throw new ApiError("User with this email already exists", 409, "CONFLICT");
		}
		const user: User = {
			id: `usr_mock_${Date.now()}`,
			email: input.email,
			phone: input.phone,
			name: input.name,
			role: input.role,
			onboardingStatus: input.onboardingStatus ?? "active",
			createdAt: new Date().toISOString(),
			...(input.platformRoleKey && { platformRole: { key: input.platformRoleKey, name: input.platformRoleKey, system: false } }),
		};
		fixtures = [user, ...fixtures];
		return user;
	},
	async update(id, patch) {
		await sleep(250);
		const current = find(id);
		const next: User = {
			...current,
			...(patch.name !== undefined && { name: patch.name }),
			...(patch.phone !== undefined && { phone: patch.phone }),
			...(patch.shopName !== undefined && {
				shopName: patch.shopName.trim() || undefined,
			}),
			...(patch.role !== undefined && { role: patch.role }),
			...(patch.onboardingStatus !== undefined && {
				onboardingStatus: patch.onboardingStatus,
			}),
			...(patch.platformRoleKey !== undefined && {
				platformRole: patch.platformRoleKey
					? { key: patch.platformRoleKey, name: patch.platformRoleKey, system: false }
					: undefined,
			}),
		};
		fixtures = fixtures.map((u) => (u.id === id ? next : u));
		return next;
	},
	async changePassword(id, input) {
		await sleep(250);
		if (input.password.trim().length < 8) {
			throw new ApiError("Password must be at least 8 characters", 400, "VALIDATION_ERROR");
		}
		return find(id);
	},
	async block(id, reason) {
		await sleep(150);
		const next = { ...find(id), blockedAt: new Date().toISOString(), blockedReason: reason };
		fixtures = fixtures.map((u) => (u.id === id ? next : u));
		return next;
	},
	async unblock(id) {
		await sleep(150);
		const next = { ...find(id), blockedAt: undefined, blockedReason: undefined };
		fixtures = fixtures.map((u) => (u.id === id ? next : u));
		return next;
	},
	async staff(id) {
		await sleep(150);
		return memberships.filter((member) => member.userId === id);
	},
	async buyerAnalytics(id) {
		// Офлайн-заглушка: реальные числа приходят с бэкенда.
		await sleep(150);
		return {
			userId: id,
			activity: "active",
			ordersTotal: 0,
			ordersPaid: 0,
			ordersCancelled: 0,
			gmv: 0,
			avgCheck: 0,
			topShops: [],
			orders: [],
		};
	},
	async topBuyers() {
		// Офлайн-заглушка: реальный рейтинг приходит с бэкенда.
		await sleep(150);
		return { items: [] };
	},
};
